import { useEffect, useRef, useState, useId } from 'react';
import mermaid from 'mermaid';
import styles from './CodeBlock.module.css';

export default function MermaidDiagram({ chart }) {
  const containerRef = useRef(null);
  const [svg, setSvg] = useState('');
  const [error, setError] = useState(null);
  const reactId = useId().replace(/:/g, '');

  useEffect(() => {
    let isCancelled = false;

    const renderDiagram = async () => {
      try {
        setError(null);
        const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
        
        mermaid.initialize({
          startOnLoad: false,
          theme: isDark ? 'dark' : 'neutral',
          securityLevel: 'loose',
          fontFamily: 'Barlow, IBM Plex Sans, sans-serif',
        });

        const id = `mermaid-${reactId}-${Date.now()}`;
        const cleanChart = chart.trim();
        const { svg: renderedSvg } = await mermaid.render(id, cleanChart);

        if (!isCancelled) {
          setSvg(renderedSvg);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err.message || 'Failed to render Mermaid diagram.');
        }
      }
    };

    renderDiagram();

    const handleThemeChange = () => {
      renderDiagram();
    };

    window.addEventListener('yacc-theme-change', handleThemeChange);
    return () => {
      isCancelled = true;
      window.removeEventListener('yacc-theme-change', handleThemeChange);
    };
  }, [chart, reactId]);

  if (error) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <span className={styles.languageBadge}>Mermaid (Error)</span>
        </div>
        <pre className={styles.pre}>
          <code>{chart}</code>
        </pre>
        <p className="meta" style={{ padding: '0.5rem 1rem', color: 'var(--color-accent)' }}>
          {error}
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        margin: '1.5rem 0',
        padding: '1.5rem',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-line)',
        background: 'var(--color-paper-card)',
        overflowX: 'auto',
        display: 'flex',
        justifyContent: 'center',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div
        ref={containerRef}
        dangerouslySetInnerHTML={{ __html: svg }}
        style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
      />
    </div>
  );
}
